import { InvalidWorkbookError } from "./InvalidWorkbookError";
import { ZipArchive } from "./zip";

// Reads the cell text out of an .xlsx. Deliberately narrow: no styles, no number formats and no
// dates - every column we import is text or a plain number, and the cached value of a formula is
// already in the file. Anything richer belongs in a spreadsheet library, not here.

export interface XlsxSheet {
  name: string;
  rows: (string | null)[][];
}

const parseXml = (xml: string): Document => {
  const document = new DOMParser().parseFromString(xml, "application/xml");
  if (document.getElementsByTagName("parsererror").length > 0) throw new InvalidWorkbookError();
  return document;
};

const textOf = (element: Element | null): string => (element?.textContent ?? "").toString();

// "AA" -> 26. Excel columns are base-26 with no zero.
const columnIndexFromRef = (ref: string): number | null => {
  const letters = /^([A-Z]+)/.exec(ref.toUpperCase());
  if (!letters) return null;

  let index = 0;
  for (let position = 0; position < letters[1].length; position += 1) {
    index = index * 26 + (letters[1].charCodeAt(position) - 64);
  }
  return index - 1;
};

// Most text lives in one shared table; cells hold an index into it.
const readSharedStrings = async (archive: ZipArchive): Promise<string[]> => {
  const xml = await archive.readText("xl/sharedStrings.xml");
  if (!xml) return [];

  const items = parseXml(xml).getElementsByTagName("si");
  return Array.from(items).map((item) => {
    // A single <si> can be split into several <t> runs by formatting; the value is their concatenation.
    const runs = item.getElementsByTagName("t");
    return Array.from(runs)
      .map((run) => textOf(run))
      .join("");
  });
};

const cellValue = (cell: Element, sharedStrings: string[]): string | null => {
  const type = cell.getAttribute("t");

  if (type === "inlineStr") {
    const runs = cell.getElementsByTagName("t");
    return Array.from(runs).length === 0
      ? null
      : Array.from(runs)
          .map((run) => textOf(run))
          .join("");
  }

  const value = cell.getElementsByTagName("v")[0];
  if (!value) return null;
  const raw = textOf(value);

  if (type === "s") {
    const index = Number(raw);
    return Number.isInteger(index) ? (sharedStrings[index] ?? null) : null;
  }
  if (type === "b") return raw === "1" ? "TRUE" : "FALSE";

  // "str" is a cached formula string; anything else is a number written as text.
  return raw;
};

const readSheet = (xml: string, sharedStrings: string[]): (string | null)[][] => {
  const rowElements = parseXml(xml).getElementsByTagName("row");
  const bySpreadsheetRow = new Map<number, (string | null)[]>();
  let lastRow = 0;

  Array.from(rowElements).forEach((rowElement, rowOrder) => {
    // Excel omits empty rows entirely, so the r attribute - not the element order - decides where a
    // row sits. Losing that would shift every row number we report back to the user.
    const declared = Number(rowElement.getAttribute("r"));
    const rowNumber = Number.isInteger(declared) && declared > 0 ? declared : rowOrder + 1;

    const cells: (string | null)[] = [];
    Array.from(rowElement.getElementsByTagName("c")).forEach((cell, cellOrder) => {
      const ref = cell.getAttribute("r");
      const column = ref ? columnIndexFromRef(ref) : null;
      const index = column ?? cellOrder;

      while (cells.length < index) cells.push(null);
      cells[index] = cellValue(cell, sharedStrings);
    });

    bySpreadsheetRow.set(rowNumber, cells);
    lastRow = Math.max(lastRow, rowNumber);
  });

  const rows: (string | null)[][] = [];
  for (let rowNumber = 1; rowNumber <= lastRow; rowNumber += 1) {
    rows.push(bySpreadsheetRow.get(rowNumber) ?? []);
  }
  return rows;
};

// workbook.xml lists the sheets in tab order but refers to them by relationship id; the rels file
// turns those into part names.
const readSheetParts = async (archive: ZipArchive): Promise<{ name: string; path: string }[]> => {
  const workbookXml = await archive.readText("xl/workbook.xml");
  const relsXml = await archive.readText("xl/_rels/workbook.xml.rels");
  if (!workbookXml || !relsXml) return [];

  const targets = new Map<string, string>();
  Array.from(parseXml(relsXml).getElementsByTagName("Relationship")).forEach((relationship) => {
    const id = relationship.getAttribute("Id");
    const target = relationship.getAttribute("Target");
    if (id && target) targets.set(id, target.replace(/^\/?xl\//, "").replace(/^\//, ""));
  });

  return Array.from(parseXml(workbookXml).getElementsByTagName("sheet"))
    .map((sheet) => {
      const relationshipId =
        sheet.getAttribute("r:id") ??
        sheet.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id");
      const target = relationshipId ? targets.get(relationshipId) : undefined;

      return { name: sheet.getAttribute("name") ?? "", path: target ? `xl/${target}` : "" };
    })
    .filter((sheet) => sheet.path !== "");
};

export const readXlsxWorkbook = async (data: ArrayBuffer): Promise<XlsxSheet[]> => {
  const archive = new ZipArchive(data);

  const sharedStrings = await readSharedStrings(archive);
  let parts = await readSheetParts(archive);

  // A workbook without usable relationships still has its sheets on disk in order.
  if (parts.length === 0) {
    parts = archive
      .names()
      .filter((name) => /^xl\/worksheets\/sheet\d+\.xml$/.test(name))
      .sort((a, b) => Number(/(\d+)/.exec(a)?.[1] ?? 0) - Number(/(\d+)/.exec(b)?.[1] ?? 0))
      .map((path) => ({ name: path, path }));
  }

  if (parts.length === 0) throw new InvalidWorkbookError();

  const sheets: XlsxSheet[] = [];
  for (let index = 0; index < parts.length; index += 1) {
    // Sequential: each part is inflated from the same archive.
    // eslint-disable-next-line no-await-in-loop
    const xml = await archive.readText(parts[index].path);
    if (xml) sheets.push({ name: parts[index].name, rows: readSheet(xml, sharedStrings) });
  }

  return sheets;
};
